import React, { useState, useEffect } from "react";
import { LocalizedLink } from "@/i18n/LocalizedLink";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { imgUrl } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useLang } from "@/i18n/LangContext";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { Library, Shield, Loader2, Copy, Eye, EyeOff, X, RotateCw, AlertCircle, Mail, User as UserIcon, Calendar, Key } from "lucide-react";

export default function Profile() {
  const { t } = useLang();
  const { user, loading: userLoading } = useUserAuth();
  const qc = useQueryClient();
  const [revealId, setRevealId] = useState(null);
  const [guardItem, setGuardItem] = useState(null);
  const [guardResult, setGuardResult] = useState(null);
  const [guardLoading, setGuardLoading] = useState(false);

  const { data: library = [], isLoading } = useQuery({
    queryKey: ["library"],
    queryFn: async () => (await api.get("/me/library")).data,
    enabled: !!user,
  });

  if (userLoading) return <main className="min-h-[60vh] grid place-items-center text-zinc-500">{t("common.loading")}</main>;
  if (!user) return <Navigate to="/signin?next=/profile" replace />;

  const copy = async (v) => {
    const ok = await copyToClipboard(v);
    if (ok) toast.success(t("verify.copied"));
    else toast.error("Copy failed");
  };

  const fetchGuard = async (item, challengeSessionId = null) => {
    setGuardItem(item);
    setGuardLoading(true);
    if (!challengeSessionId) setGuardResult(null);
    try {
      const r = await api.post(
        `/me/library/${item.delivery_id}/steam-guard`,
        challengeSessionId ? { challenge_session_id: challengeSessionId } : {},
      );
      setGuardResult(r.data);
      qc.invalidateQueries({ queryKey: ["library"] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Cooldown response from backend (429)
      if (err?.response?.status === 429 && detail && typeof detail === "object") {
        setGuardResult({
          found: false,
          cooldown: true,
          retry_after_seconds: detail.retry_after_seconds,
          unlock_at: detail.unlock_at,
          message: detail.message,
        });
        qc.invalidateQueries({ queryKey: ["library"] });
      } else {
        setGuardResult({ found: false, message: (typeof detail === "string" ? detail : t("common.error")) });
      }
    } finally {
      setGuardLoading(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="profile-page">
      {/* Header card */}
      <div className="bg-[#0c0c0c] border border-white/5 rounded-2xl p-6 md:p-8 mb-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2 flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand to-brand-cyan flex items-center justify-center text-white font-bold text-2xl glow-blue">
            {user.username[0].toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold mb-1" data-testid="profile-username">{user.username}</h1>
            <div className="text-sm text-zinc-500 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {user.email}
            </div>
          </div>
        </div>
        <div className="bg-[#080808] border border-white/5 rounded-lg p-4 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Games Owned</div>
          <div className="font-heading text-4xl font-bold text-brand-cyan tabular-nums" data-testid="library-count">{library.length}</div>
        </div>
      </div>

      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2 flex items-center gap-2">
          <Library className="w-3.5 h-3.5" /> {t("profile.title")}
        </div>
        <h2 className="font-heading text-3xl font-bold mb-2">{t("profile.title")}</h2>
        <p className="text-zinc-400">{t("profile.subtitle")}</p>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">{t("common.loading")}</div>
      ) : library.length === 0 ? (
        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-12 text-center">
          <Library className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 mb-6">{t("profile.empty")}</p>
          <LocalizedLink to="/verify" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-2.5 rounded-lg glow-blue" data-testid="redeem-first-cta">
            <Key className="w-4 h-4" /> {t("profile.redeem_first")}
          </LocalizedLink>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {library.map((item) => (
            <LibraryCard
              key={item.delivery_id}
              item={item}
              revealed={revealId === item.delivery_id}
              onToggleReveal={() => setRevealId(revealId === item.delivery_id ? null : item.delivery_id)}
              onCopy={copy}
              onFetchGuard={() => fetchGuard(item)}
            />
          ))}
        </div>
      )}

      {/* Steam Guard Modal */}
      <AnimatePresence>
        {guardItem && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="steam-guard-modal">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#0c0c0c] border border-white/10 rounded-2xl p-8 max-w-md w-full pulse-glow">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">{t("verify.guard_title")}</div>
                <button onClick={() => setGuardItem(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="text-sm text-zinc-400 mb-4">{guardItem.title}</div>
              {guardLoading ? (
                <div className="py-12 flex flex-col items-center gap-4">
                  <ScrambleCode />
                  <div className="text-sm text-zinc-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("verify.guard_fetching")}</div>
                </div>
              ) : guardResult?.found ? (
                <div className="py-8 text-center">
                  <div className="font-mono text-5xl md:text-6xl font-bold tracking-[0.4em] text-white mb-4" data-testid="steam-guard-code">{guardResult.code}</div>
                  <button onClick={() => copy(guardResult.code)} className="text-sm text-brand-cyan hover:text-white inline-flex items-center gap-2"><Copy className="w-4 h-4" /> {t("verify.copy")}</button>
                  <p className="mt-5 text-xs text-zinc-500">{t("verify.cooldown_notice")}</p>
                </div>
              ) : guardResult?.needs_challenge ? (
                <SupplierChallenge
                  sessionId={guardResult.challenge_session_id}
                  message={guardResult.message}
                  onSolved={(sessionId) => fetchGuard(guardItem, sessionId)}
                />
              ) : guardResult?.cooldown ? (
                <div className="py-8 text-center" data-testid="guard-cooldown-state">
                  <Shield className="w-12 h-12 text-brand-cyan mx-auto mb-3" />
                  <p className="text-zinc-300 mb-2 font-semibold">{guardResult.message}</p>
                  <p className="text-sm text-zinc-500">{t("verify.cooldown_next")}: <span className="text-white">{formatCooldown(guardResult.retry_after_seconds, t)}</span></p>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <p className="text-zinc-300 mb-6">{guardResult?.message || t("verify.no_code")}</p>
                  <button onClick={() => fetchGuard(guardItem)} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2" data-testid="guard-retry-btn">
                    <RotateCw className="w-4 h-4" /> {t("verify.guard_retry")}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function LibraryCard({ item, revealed, onToggleReveal, onCopy, onFetchGuard }) {
  const { t } = useLang();
  const onCooldown = item.guard_cooldown_seconds > 0;
  return (
    <div className="bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden" data-testid={`library-card-${item.delivery_id}`}>
      <div className="flex">
        <div className="w-32 h-32 md:w-40 md:h-40 bg-[#080808] flex-shrink-0 overflow-hidden">
          {item.image_url && <img src={imgUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" loading="lazy" />}
        </div>
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30 mb-2">{item.platform}</span>
              <h3 className="font-heading font-bold text-lg truncate">{item.title}</h3>
              <div className="text-xs text-zinc-500 mt-1">{t("profile.delivered_on")}: {new Date(item.delivered_at).toLocaleDateString()}</div>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <FieldRow label={t("verify.username")} value={item.steam_username} onCopy={() => onCopy(item.steam_username)} testid={`lib-username-${item.delivery_id}`} />
            <FieldRow label={t("verify.password")} value={item.steam_password} masked={!revealed} onToggle={onToggleReveal} onCopy={() => onCopy(item.steam_password)} testid={`lib-password-${item.delivery_id}`} />
          </div>
          <button
            onClick={onFetchGuard}
            disabled={onCooldown}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-brand to-brand-cyan disabled:from-zinc-800 disabled:to-zinc-800 disabled:cursor-not-allowed text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition-all enabled:hover:scale-[1.01]"
            data-testid={`steam-guard-btn-${item.delivery_id}`}
          >
            <Shield className="w-4 h-4" />
            {onCooldown ? formatCooldown(item.guard_cooldown_seconds, t) : t("verify.guard_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatCooldown(secs, t) {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  let parts = [];
  if (d > 0) parts.push(`${d}${t("verify.cooldown_d")}`);
  if (h > 0) parts.push(`${h}${t("verify.cooldown_h")}`);
  if (parts.length === 0) parts.push(`${m}${t("verify.cooldown_m")}`);
  return `${t("verify.cooldown_prefix")} ${parts.join(" ")}`;
}

function FieldRow({ label, value, masked, onCopy, onToggle, testid }) {
  return (
    <div className="flex items-center gap-2 bg-[#080808] border border-white/5 rounded px-3 py-2 text-sm">
      <span className="text-xs uppercase tracking-wider text-zinc-500 font-bold min-w-[80px]">{label}</span>
      <code className="font-mono flex-1 truncate" data-testid={testid}>{masked ? "•".repeat(Math.min(value?.length || 8, 16)) : value}</code>
      {onToggle && <button onClick={onToggle} className="text-zinc-500 hover:text-white" data-testid={`${testid}-toggle`}>{masked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>}
      <button onClick={onCopy} className="text-zinc-500 hover:text-white" data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ScrambleCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";
  const [v, setV] = useState("XXXXX");
  useState(() => null);
  if (typeof window !== "undefined" && !window._sc) {
    window._sc = true;
  }
  // simple interval via setTimeout chain
  const React = require("react");
  React.useEffect(() => {
    const id = setInterval(() => {
      setV(Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join(""));
    }, 70);
    return () => clearInterval(id);
  }, []);
  return <div className="font-mono text-5xl md:text-6xl font-bold tracking-[0.4em] text-brand-cyan/60">{v}</div>;
}

function SupplierChallenge({ sessionId, message, onSolved }) {
  const [frame, setFrame] = React.useState(null);
  const [status, setStatus] = React.useState("waiting");
  React.useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const state = (await api.get(`/me/supplier-challenges/${sessionId}`)).data;
        if (stopped) return;
        if (state.status === "complete") { onSolved(sessionId); return; }
        if (state.status !== "challenge") { setStatus(state.message || "Security check ended."); return; }
        setFrame(state);
      } catch (_) { if (!stopped) setStatus("Security session ended."); }
    };
    poll();
    const id = setInterval(poll, 900);
    return () => { stopped = true; clearInterval(id); };
  }, [sessionId, onSolved]);
  const click = async (e) => {
    if (!frame) return;
    const rect = e.currentTarget.getBoundingClientRect();
    await api.post(`/me/supplier-challenges/${sessionId}/click`, {
      x: (e.clientX - rect.left) * (frame.width / rect.width),
      y: (e.clientY - rect.top) * (frame.height / rect.height),
    });
    setStatus("Checking…");
  };
  return (
    <div className="py-4 text-center" data-testid="supplier-challenge">
      <p className="text-zinc-300 mb-4">{message || "Complete the supplier security check."}</p>
      {frame ? <img src={`data:image/jpeg;base64,${frame.frame}`} alt="Supplier security check" onClick={click} className="mx-auto max-w-full cursor-pointer rounded" /> : <Loader2 className="mx-auto animate-spin" />}
      {status !== "waiting" && <p className="mt-3 text-xs text-zinc-400">{status}</p>}
    </div>
  );
}
